import styled, { css } from 'styled-components';

import { rem } from 'approot/shared/style';
import {
  baseFlexCss,
  flexCentreAlignCss,
  flexColumnCss,
} from 'approot/shared/styles/flex.style';
import {
  grey1,
  grey10,
  grey5,
  grey9,
  purple7,
} from 'approot/shared/styles/colours.style';
import { imageTransition } from 'approot/shared/images/images.style';
import { borderRadius8 } from 'approot/shared/styles/borders.style';
import {
  font14,
  fontSize12,
  fontSize14,
} from 'approot/shared/styles/typography.style';
import {
  spacing16,
  spacing4,
  spacing8,
} from 'approot/shared/styles/spacing.style';
import { COLOURS } from '../../interactive-response/interactive-response-shared.constants';

export const DRAG_ITEM_SIZE = 124;

const baseDragItem = css`
  background-color: ${grey1};
  border: 1px solid ${grey10};
  border-radius: ${rem(borderRadius8)};
  box-shadow: 4px 4px 4px -4px #162233;
  width: 100%;
  height: 100%;
  position: relative;
  transition:
    width 0.3s ease-out,
    height 0.3s ease-out;
`;

type BaseDragItemBaseColourProps = {
  $baseColour?: string | null;
};
export const baseDragItemBaseColour = css<BaseDragItemBaseColourProps>`
  ${({ $baseColour }) =>
    $baseColour &&
    `
      background-color: ${
        COLOURS.find(colour => colour.base === $baseColour)?.colour1
      };
      border-color: ${
        COLOURS.find(colour => colour.base === $baseColour)?.colour2
      };
    `}
`;

type InteractiveDragItemProps = {
  $isDragging?: boolean;
  $isDisabled?: boolean;
  $isKeyboardDragging?: boolean;
  $baseColour?: string | null;
};
export const InteractiveDragItem = styled.div<InteractiveDragItemProps>`
  ${flexCentreAlignCss}
  ${baseDragItem}
  ${baseDragItemBaseColour}
      
  cursor: grab;

  &:focus {
    outline: 2px solid ${grey10};
    outline-offset: 2px;
  }

  ${({ $isDragging }) =>
    $isDragging &&
    `
      background-color: ${grey5};
      opacity: 0;
    `}

  ${({ $isDisabled }) =>
    $isDisabled &&
    `
      cursor: not-allowed;
    `}

  ${({ $isKeyboardDragging }) =>
    $isKeyboardDragging &&
    `
      &:focus {
        box-shadow: 1px 0px 15px rgba(0,0,0,1)
      }
      box-shadow: 1px 0px 15px rgba(0,0,0,1)
    `}
`;

export const InteractiveClickItem = styled.div<InteractiveDragItemProps>`
  ${flexCentreAlignCss}
  ${baseDragItem}
  ${baseDragItemBaseColour}
      
  cursor: pointer;
  border: 1px solid ${purple7};
  box-shadow: 0 0 0 1px ${purple7};

  &:focus {
    outline: 2px solid ${grey10};
    outline-offset: 2px;
  }
`;

type InteractiveDisplayItemProps = {
  $baseColour?: string | null;
};
export const InteractiveDisplayItem = styled.div<InteractiveDisplayItemProps>`
  ${flexCentreAlignCss}
  ${baseDragItem}
  ${baseDragItemBaseColour}
`;

type InteractiveItemImageProps = {
  $imageUrl?: string | null;
  $imageHasLoaded?: boolean;
};
export const InteractiveItemImage = styled.div.attrs<InteractiveItemImageProps>(
  ({ $imageUrl }) => ({
    style: {
      backgroundImage: $imageUrl ? `url(${$imageUrl})` : '',
    },
  })
)<InteractiveItemImageProps>`
  ${imageTransition}

  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  background-color: ${grey1};
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  opacity: ${({ $imageHasLoaded = true }) => ($imageHasLoaded ? '1' : '0')};
  position: absolute;
  // this is to replace overflow hidden on the parent
  // which we can't have now due to the kebab menu
  // -1px is to account for border of parent
  border-radius: ${rem(borderRadius8 - 1)};
`;

type InteractiveDragContainerProps = {
  $scaleTo?: number;
};
export const InteractiveDragContainer = styled.div<InteractiveDragContainerProps>`
  position: relative;
  min-width: ${rem(DRAG_ITEM_SIZE)};
  min-height: ${rem(DRAG_ITEM_SIZE)};
  width: ${rem(DRAG_ITEM_SIZE)};
  height: ${rem(DRAG_ITEM_SIZE)};

  ${({ $scaleTo }) =>
    $scaleTo &&
    `
      transform: scale(${$scaleTo / DRAG_ITEM_SIZE});
      transform-origin: top;
    `}
`;

export const InteractiveTooltip = styled.div`
  ${baseFlexCss}
  color: ${grey1};
  background-color: ${grey9};
  box-shadow:
    0px 4px 8px -4px rgba(22, 34, 51, 0.08),
    0px 16px 24px rgba(22, 34, 51, 0.08);
  border-radius: ${rem(borderRadius8)};
  gap: ${rem(spacing8)};
  font-size: ${rem(fontSize14)};
  padding: ${rem(spacing8)} ${rem(spacing16)};

  svg {
    path {
      stroke: ${grey1};
    }
  }
`;

type InteractiveTextProps = {
  $baseColour?: string | null;
};
export const InteractiveText = styled.div<InteractiveTextProps>`
  ${font14}
  ${flexColumnCss}

  height: 100%;
  padding: ${rem(spacing8)};

  ${({ $baseColour }) =>
    $baseColour &&
    `
      color: ${COLOURS.find(colour => colour.base === $baseColour)?.colour2};
    `}

  p {
    margin: 0;
    text-align: center;
  }
`;

export const InteractiveTextImageContainer = styled.div`
  background-color: rgba(255, 255, 255, 0.8);
  border-radius: ${rem(2)};
  border: 1px solid ${grey5};
  color: ${grey10};
  padding: 0 ${rem(spacing4)};
  position: absolute;
  left: ${rem(spacing4)};
  font-size: ${rem(fontSize12)};
  line-height: ${rem(14)}; // override default line-height
  right: ${rem(spacing4)};
  bottom: ${rem(spacing4)};
  text-align: center;
`;

export const InteractiveExpandTrigger = styled.div`
  position: absolute;
  inset: 0;
`;

export const InteractiveDragItemButtonContainer = styled.div`
  position: absolute;
  top: ${rem(spacing4)};
  right: ${rem(spacing4)};
`;
